import React, { useState, useEffect, memo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate  } from 'react-router';
import queryString from 'query-string';
import { dataActions } from '@/store/actions/data.actions';
import parseObjectToQuery from '@/utils/functions/parseObjectToQuery';
import useWindowIsMobile from '@/utils/hooks/useWindowIsMobile';
import { CanHandleDepartments } from '@/utils/permissions/permissions';

import SearchInput from '@/views/components/SearchInput/SearchInput';

import CustomSelect from '@/views/components/CustomSelect/CustomSelect';

import LettersDeptAutoSuggest from '@/views/components/AutoSuggestInput/LettersDeptAutoSuggest';

import styles from '@/views/pages/Users/Users.module.scss';

const UsersSearchFilters = ({ resetSort }) => {
	const location = useLocation();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const { dict, isMimAuthAvailable, mode } = useSelector((state) => state.app);
	const user = useSelector((state) => state.auth.user);

	const { userRoles, accountTypes } = useSelector(state => state.data);

	const [ hideFilters, setHideFilters ] = useState(false);

	const isMobile = useWindowIsMobile(767);

	const userRole = user && user.role;

	const [ searchedQuery, setSearchedQuery ] = useState('');
	const [ query, setQuery ] = useState('');

	const [ selectedAccountTypeStartValue, setSelectedAccountTypeStartValue ] = useState({ label: dict.users.accountTypes.all, value: 'none' });
	const [ selectedRoleStartValue, setSelectedRoleStartValue ] = useState({ label: dict.users.roles.all, value: 'none' });

	const [ selectAccountTypes, setSelectAccountTypes ] = useState([]);
	const [ selectRoles, setSelectRoles ] = useState([]);

	const searchQuery = (searchString = null) => {
		const searchParams = queryString.parse(location.search);
		const copiedSearchParams = Object.assign({}, searchParams);
		if (searchString === null || searchString.length === 0) {
			if (Object.keys(copiedSearchParams).indexOf('q') !== -1) {
				delete copiedSearchParams.q;
				navigate(`${location.pathname}?${parseObjectToQuery(copiedSearchParams)}`);
			}
		} else {
			resetSort();
			delete copiedSearchParams.ord;
			navigate(`${location.pathname}?q=${encodeURIComponent(searchString)}&page=1`);
		}
	};

	useEffect(() => {
		if (!userRoles) dispatch(dataActions.getUserRoles());
		if (!accountTypes) dispatch(dataActions.getAccountTypes());
		const qu = queryString.parse(location.search);
		if (Object.keys(qu).indexOf('q') !== -1) {
			setQuery(qu.q);
			setSearchedQuery(qu.q);
		}
	}, []);

	useEffect(() => {
		if (userRoles && selectRoles.length < 1) {
			setSelectRoles([ { label: dict.users.roles.all, value: 'none' },
				...userRoles.map(item => ({
					value: item, label: dict.users.roles[item.replaceAll("-", "")],
				}) ),
			]);
		}

		if (selectRoles.length > 1) {
			let roleName = new URLSearchParams(location.search).get("role");
			if (roleName) {
				setSelectedRoleStartValue(selectRoles.find(item => item.value === roleName));
			} else {
				setSelectedRoleStartValue(selectRoles[0]);
			}
		}
	}, [ location.search, selectRoles, userRoles, hideFilters ]);

	useEffect(() => {
		if (accountTypes && selectAccountTypes.length < 1) {
			setSelectAccountTypes([ { label: dict.users.accountTypes.all, value: 'none' },
				...accountTypes.map(item => ({
					value: item, label: dict.users.accountTypes[item.replaceAll("-", "")],
				}) ),
			]);
		}

		if (selectAccountTypes.length > 1) {
			let typeName = new URLSearchParams(location.search).get("provider");
			if (typeName) {
				setSelectedAccountTypeStartValue(selectAccountTypes.find(item => item.value === typeName));
			} else {
				setSelectedAccountTypeStartValue(selectAccountTypes[0]);
			}
		}
	}, [ location.search, selectAccountTypes, accountTypes, hideFilters, isMimAuthAvailable ]);

	useEffect(() => {
		const searchParams = queryString.parse(location.search);
		const copiedSearchParams = Object.assign({}, searchParams);
		if (copiedSearchParams.q) {
			setQuery(copiedSearchParams.q);
			setSearchedQuery(copiedSearchParams.q);
		} else {
			setQuery("");
			setSearchedQuery("");
		}
	}, [ location ]);

	const onSelectAccountType = (type) => {
		setSelectedAccountTypeStartValue(type);
		const urlParams = new URLSearchParams(location.search);
		if (type.value === 'none') {
			urlParams.delete('provider');
		} else {
			urlParams.set('provider', type.value);
		}
		navigate(`${location.pathname}?${urlParams}`);
	};

	const onSelectRole = (role) => {
		setSelectedRoleStartValue(role);
		const urlParams = new URLSearchParams(location.search);
		if (role.value === 'none') {
			urlParams.delete('role');
		} else {
			urlParams.set('role', role.value);
		}
		navigate(`${location.pathname}?${urlParams}`);
	};

	return (
		<section className={ styles['users__searchbar'] }>
			<div className={ styles['users__searchbar--item'] }>
				<SearchInput
					label={ dict.users.list.searchLabel }
					placeholder={ "" }
					searchedValue={ searchedQuery }
					value={ query }
					setValue={ setQuery }
					searchFunction={ searchQuery }
				/>
			</div>
			<div className={ styles['users__searchbar--item'] }>
				{(!hideFilters || !isMobile) && <div className={ `${styles['users__searchbar--filters']} ${!CanHandleDepartments(userRole, mode) ? styles['users__searchbar--filters-standalone'] : null }` }>
					{CanHandleDepartments(userRole, mode) && <LettersDeptAutoSuggest 
						label={ dict.letters.searchByDepartment } 
						placeholder={ dict.letters.autocompletePlaceholder }
					/>}

					{isMimAuthAvailable && (
						<CustomSelect
							label={ "Rodzaj konta" }
							options={ selectAccountTypes }
							value={ selectedAccountTypeStartValue }
							onChange={ onSelectAccountType }
						/>
					)}

					<CustomSelect
						label={ "Rola" }
						options={ selectRoles }
						value={ selectedRoleStartValue }
						onChange={ onSelectRole }
					/>
				</div>}
				{isMobile && <div 
					className={ styles['users__searchbar--hide-button'] }
					onClick={ () => {
						setHideFilters(!hideFilters);
					} }
				>
					{ !hideFilters ? 
						<>Ukryj filtry <i className="icon-arrow-up-full" /></> : 
						<>Pokaż filtry <i className="icon-arrow-down-full" /></>
					}
				</div>}
			</div>
		</section>
	);
};

export default memo(UsersSearchFilters);